import { ArrowLeft } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ButtonCmp from '../../components/ButtonCmp';
import SelectBox from '../../components/SelectBox/SelectBox';
import { ROUTES } from '../../constants/routes';
import { setIsAuthenticated } from '../../redux/auth.slice';
import { type AppDispatch } from '../../redux/store';
import { getWalCustomers, walSelect } from '../../services/AuthService';
import WalToast from '../../utils/WalToast';
import AuthWrapper from '../AppLayout/AuthWrapper';

// Skeleton options shown while the customer list is loading (mirrors WAL Orders filter pattern)
const dummyCustomerList = Array(5)
    .fill(null)
    .map((_, index) => ({
        value: (index + 1).toString(),
        label: `Customer ${index + 1}`,
    }));

interface IProps {
    onBack: () => void;
}

// WAL internal-user customer selection (Microsoft SSO flow) — the user picks ONLY a customer and
// logs in as that customer directly. The list is role-scoped by the backend (admin: all, manager:
// team members' customers, sales: own customers). Replaces the legacy super-admin impersonation.
const CustomerSelection = ({ onBack }: IProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Customer search state
    const [searchCustomer, setSearchCustomer] = useState<string>('');
    const [customerListOptions, setCustomerListOptions] = useState<any[]>([]);
    const [isCustomerLoading, setIsCustomerLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

    // Submit state
    const [isLoading, setIsLoading] = useState(false);

    // Debounced customer search — same 250 ms pattern as WAL Orders filter
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        abortRef.current?.abort();
        abortRef.current = new AbortController();

        const delay = setTimeout(() => {
            setIsCustomerLoading(true);
            getWalCustomers({ search: searchCustomer, limit: 10 })
                .then((res: any) => {
                    const opts = (res?.data || []).map((c: any) => ({
                        value: c.id,
                        label: c.name,
                        // Build the full image URL the same way WAL does (imageUrl + image)
                        image: c.imageUrl && c.image ? `${c.imageUrl}${c.image}` : null,
                        isImage: true,
                    }));
                    setCustomerListOptions(opts);
                })
                .catch(() => {
                    // AbortErrors are expected on rapid keystrokes — only toast real failures
                    if (!abortRef.current?.signal.aborted) {
                        WalToast.error('Failed to load customers.');
                    }
                })
                .finally(() => setIsCustomerLoading(false));
        }, 250);

        return () => {
            clearTimeout(delay);
            abortRef.current?.abort();
        };
    }, [searchCustomer]);

    const handleLogin = () => {
        if (!selectedCustomer?.value) return;

        setIsLoading(true);
        walSelect({ customerMasterId: selectedCustomer.value })
            .then((res: any) => {
                if (res?.token) {
                    dispatch(setIsAuthenticated(true) as any);
                    // WAL sessions never need account setup — go straight to the dashboard
                    navigate(ROUTES.HOME);
                }
            })
            .catch((error: any) => WalToast.error(error?.response?.data?.message || 'Login failed. Please try again.'))
            .finally(() => setIsLoading(false));
    };

    return (
        <AuthWrapper authHeading="Select Customer" authSubTitle="Choose the customer you want to access the portal as.">
            <div className="flex flex-col gap-y-5">
                <SelectBox
                    label="Customer"
                    labelClassName="form_label"
                    name="customer"
                    id="customer"
                    options={isCustomerLoading ? dummyCustomerList : customerListOptions}
                    onChangeFunc={(option: any) => setSelectedCustomer(option)}
                    value={selectedCustomer}
                    isSearchable={true}
                    isClearable={true}
                    isOptionLoading={isCustomerLoading}
                    showCustomerImage={true}
                    noOptionMessage="No customer found"
                    placeholder="Select a customer"
                    onInputChange={(e: any, { action }: any) => {
                        if (action === 'input-change' || (action === 'input-blur' && e?.trim())) {
                            if (e?.trim()) setIsCustomerLoading(true);
                            setSearchCustomer(e);
                        }
                    }}
                    onMenuClose={() => setSearchCustomer('')}
                    onClearFunc={() => {
                        setSearchCustomer('');
                        setSelectedCustomer(null);
                    }}
                />

                <ButtonCmp type="button" size="lg" disabled={!selectedCustomer || isLoading} className="btn_primary_black w-full mt-1" loading={isLoading} onClick={handleLogin}>
                    Login
                </ButtonCmp>

                <div className="text-center">
                    <span className="text-grayLight600 gap-x-1.5 cursor-pointer inline-flex items-center justify-center" onClick={onBack}>
                        <ArrowLeft className="w-4 h-4 object-contain text-grayLight600" />
                        <span className="text-grayLight600 text-[12px] leading-[18px] font-semibold">{t('Auth.Verify_Email.Back_To_Login')}</span>
                    </span>
                </div>
            </div>
        </AuthWrapper>
    );
};

export default CustomerSelection;
